import { FC, FormEvent, useCallback } from 'react';

import { TErrorDetails, TWarningModalProps } from './WarningModal.constants';
import CancelButton from '@3.0/Buttons/Button/CancelButton';
import SubmitButton from '@3.0/Buttons/Button/SubmitButton';
import PrimaryButtonLink from '@3.0/Buttons/Button/PrimaryButtonLink';
import { ErrorBoundary } from '@components/ErrorBoundary';
import MoreDetails from './MoreDetails';
import ActionButtonsContainer from 'Modules/Studio/components/StudioModal/ActionButtonsContainer';

type TProps = Pick<
  TWarningModalProps,
  | 'text'
  | 'onConfirm'
  | 'onCancel'
  | 'hideCancelButton'
  | 'modalProps'
  | 'progressProps'
  | 'confirmBtnAsLink'
  | 'confirmBtnLinkProps'
  | 'showErrorDetails'
  | 'errorDetails'
  | 'showContinueText'
>;

const WarningModalForm: FC<TProps> = ({
  progressProps,
  text,
  onCancel,
  onConfirm,
  modalProps,
  showErrorDetails = false,
  errorDetails,
  hideCancelButton,
  showContinueText,
  confirmBtnAsLink,
  confirmBtnLinkProps,
}) => {
  const handleSubmit = useCallback(
    (evt: FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      if (onConfirm) {
        onConfirm();
      }
    },
    [onConfirm]
  );

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={`${modalProps?.modalContainerProps?.['data-testid'] || 'warning-modal'}-form`}
      className="warning-modal"
    >
      <ErrorBoundary>
        <section className="tw:flex-col tw:flex-auto tw:border-t tw:border-primary-400 tw:p-[40px] tw:min-h-[200px] tw:max-h-[400px] tw:overflow-y-auto">
          <div
            role="dialog"
            aria-labelledby="warningModalTitle"
            aria-describedby="warningModalDescription"
            className="tw:text-center tw:text-wrap tw:flex tw:flex-col tw:items-center"
          >
            <ErrorBoundary>
              <div
                className="tw:pb-6 fs-18 tw:leading-[1.2] tw:whitespace-normal tw:font-normal"
                id="warningModalTitle"
              >
                {text.messages.primary}
              </div>
              <div
                className="tw:whitespace-normal tw:leading-[1.2] tw:text-base tw:font-normal"
                id="warningModalDescription"
              >
                {text.messages.secondary}
              </div>
            </ErrorBoundary>

            {showContinueText && !showErrorDetails && (
              <div className="tw:leading-[1.2] tw:text-base tw:font-normal tw:mt-6">
                Do you wish to continue?
              </div>
            )}
            <ErrorBoundary>
              <MoreDetails
                showErrorDetails={showErrorDetails}
                errorDetails={errorDetails as TErrorDetails}
              />
            </ErrorBoundary>
          </div>
        </section>
      </ErrorBoundary>

      <ActionButtonsContainer className="tw:justify-end" isLegacy={false}>
        {!hideCancelButton && (
          <CancelButton
            data-testid="warning-modal-cancel-button"
            onClick={onCancel}
            label={text.buttons.cancel}
            disabled={progressProps?.isSaving}
          />
        )}

        {confirmBtnAsLink ? (
          <PrimaryButtonLink
            {...{ ...confirmBtnLinkProps, to: confirmBtnLinkProps?.to || '' }}
            data-testid="warning-modal-continue-button"
            label={text.buttons.primary}
          />
        ) : (
          <SubmitButton
            data-testid="warning-modal-continue-button"
            labelVariant={text.buttons.primary}
            disabled={progressProps?.isSaving}
          />
        )}
      </ActionButtonsContainer>
    </form>
  );
};

export default WarningModalForm;
